import {
  BooleanField,
  Button,
  Confirm,
  DateField,
  EditButton,
  FunctionField,
  NumberField,
  ReferenceField,
  ReferenceManyField,
  SelectField,
  Show,
  SimpleShowLayout,
  SingleFieldList,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useGetOne,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import { Permission, StarRatingField } from '../../components';
import { DEFAULT_API_ADMIN, PERMISSIONS } from '../../constants';
import { getChainId, post } from '../../lib';
import { ConcentratorBrandType, FillinOrigin, FillinPresetType } from '../../models';
import { formatAttendantLabel } from '../../utils';
import { FillinLogsTab } from './FillinLogsTab';
import styled from '@emotion/styled';
import { Alert, Grid, Box, Typography } from '@mui/material';
import { useState } from 'react';

const FillinTitle = () => (
  <span>{`Abastecimento`}</span>
);

const PresetValueField = ({ source }) => {
  const record = useRecordContext();

  return (
    record.presetType === FillinPresetType.cash ?
      <NumberField source={source} locales="pt-BR" options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }} />
      :
      <NumberField source={source} locales="pt-BR" emptyText="--" />
  );
}

const CONCENTRATOR_FIELDS = {
  brand: concentrator => ConcentratorBrandType.translateType(concentrator.automaticBrandType || concentrator.brandType),
  version: concentrator => concentrator.version,
  crtlVersion: concentrator => concentrator.crtlVersion,
};

const StationAutomationField = ({ field }) => {
  const record = useRecordContext();
  const placeId = record?.placeId;
  // O ReferenceField do quadro Local já semeou o cache de `getOne` desta station com o record
  // que veio do getMany, e aquele não traz `concentrators`. O `meta` dá outra chave de cache;
  // sem ele o campo nasce vazio e só preenche na revalidação.
  const { data: station } = useGetOne(
    'stations',
    { id: placeId, meta: { withConcentrators: true } },
    { enabled: !!placeId },
  );

  const values = (station?.concentrators || []).map(CONCENTRATOR_FIELDS[field]).filter(Boolean);

  return <Typography variant="body2">{values.length ? values.join(' - ') : '--'}</Typography>;
};

const UnifiedFillinNotice = () => {
  const record = useRecordContext();
  if (!record?.isUnified) return null;

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <strong>Abastecimento unificado</strong> — reúne {record.unifiedFillinCount} abastecimentos que o
      AutoSystem do posto registrou como uma venda só. Quantidade, valor e encerrante são os do
      conjunto; os pontos de todos eles estão neste registro.
    </Alert>
  );
};

const UndoUnificationButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!record?.isUnified) return null;

  const handleConfirm = async () => {
    setOpen(false);
    setLoading(true);
    try {
      const res = await post(
        `${DEFAULT_API_ADMIN}/rdp-contingencies/fillins/${record.id}/undo-unification`,
        undefined,
      );
      const r = res?.data?.[0];
      const restored = r?.restoredIds?.length ?? 0;
      // O undo já valeu; se a unificação certa não foi refeita, o usuário precisa saber.
      const notRedone = r?.remergeError || r?.skipped?.map(s => s.reason).join(', ');
      if (notRedone) {
        notify(
          'Unificação desfeita (%{restored} abastecimentos restaurados), mas a unificação certa não foi refeita: %{reason}',
          { type: 'warning', messageArgs: { restored, reason: notRedone } },
        );
      } else {
        notify(
          r?.remerged?.length
            ? 'Unificação desfeita: %{restored} abastecimentos restaurados e %{remerged} unificação(ões) refeita(s).'
            : 'Unificação desfeita: %{restored} abastecimentos restaurados.',
          { type: 'success', messageArgs: { restored, remerged: r?.remerged?.length ?? 0 } },
        );
      }
      refresh();
    } catch (e) {
      notify(e?.message || 'Erro ao desfazer a unificação', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Confirm
        isOpen={open}
        title="Desfazer unificação"
        content="Separa de volta os abastecimentos que foram juntados e refaz a unificação certa, se houver. Quantidade, valor e encerrante editados à mão neste abastecimento voltam ao que eram antes da unificação."
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
      <Button label="Desfazer unificação" color="error" onClick={() => setOpen(true)} disabled={loading} />
    </>
  );
};

const ShowActions = () => (
  <TopToolbar>
    <Permission permission={PERMISSIONS.ADMIN}>
      <UndoUnificationButton />
    </Permission>
    <Permission permission={PERMISSIONS.EDIT_FILLIN}>
      <EditButton />
    </Permission>
  </TopToolbar>
);

const FillinShow = () => {
  const { permissions } = usePermissions()
  return (
    <Show title={<FillinTitle />} actions={<ShowActions />}>
      <TabbedShowLayout>
        <Tab label="Resumo">
          <SimpleShowLayout>
            <UnifiedFillinNotice />
            <Grid container width={{ xs: '100%', xl: 1200 }} spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box component="fieldset" sx={{ border: '1px dashed grey' }}>
                  <Legend>Detalhes do abastecimento</Legend>
                  <SimpleShowLayout>
                    <TextField source="id" label="ID" />
                    {
                      Permission({
                        children: <TextField source="reserveId" label="ID da Reserva" emptyText="--" />,
                        permission: PERMISSIONS.ADMIN,
                      })
                    }
                    <ReferenceField label="Cliente" reference="customers" source="customerId" link='show'>
                      <TextField source="name" />
                    </ReferenceField>
                    <NumberField
                      source="amount"
                      label="Quantidade em litros"
                    />
                    <NumberField
                      source="value"
                      label="Valor pago"
                      options={{ style: 'currency', currency: 'BRL' }}
                    />
                    <ReferenceField label="Combustível" reference="fuels" source="fuelId">
                      <TextField source="name" />
                    </ReferenceField>
                    <FunctionField
                      label="Valor do desconto concedido"
                      render={record =>
                        `${(record.oldPricePerLiter && record.pricePerLiter
                          ? (record.oldPricePerLiter - record.pricePerLiter) *
                          (record.amount || 0)
                          : 0
                        ).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
                      }
                    />
                    <ReferenceField
                      label="Código do desconto"
                      source="discountId"
                      reference="discounts"
                      emptyText="--"
                      link={permissions && permissions.includes(PERMISSIONS.FUEL_DISCOUNTS) && "show"}
                    >
                      <TextField source="identify" />
                    </ReferenceField>
                    <NumberField
                      source="oldPricePerLiter"
                      label="Preço por litro original"
                      emptyText="--"
                      options={{
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 3,
                      }}
                    />
                    <NumberField
                      source="pricePerLiter"
                      label="Preço por litro real"
                      emptyText="--"
                      options={{
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 3,
                      }}
                    />
                    <NumberField
                      source="hoseNumber"
                      label="Número do bico"
                      emptyText="--"
                    />
                    <NumberField
                      source="encerrante"
                      label="Encerrante"
                      emptyText="--"
                    />
                    <ReferenceField source="vehicleId" reference="vehicles" label="Veículo" emptyText="--" link={false}>
                      <FunctionField render={record => [record.plate, record.brand, record.model].filter(Boolean).join(' · ')} />
                    </ReferenceField>
                    <NumberField source="odometer" label="Odômetro (km)" emptyText="--" />
                    <DateField
                      source="createdAt"
                      label="Data inclusão"
                      showTime
                    />
                    <DateField
                      source="receivedAt"
                      label="Data do recebimento"
                      showTime
                    />
                    <BooleanField
                      source="considerFillingCompletely"
                      label="Considerado tanque cheio?"
                    />
                    <SelectField source="presetType" label="Preset Limite" choices={FillinPresetType.fillinPresetTypeChoices} emptyText="--" />
                    <PresetValueField source="presetValue" label="Preset Valor" />
                  </SimpleShowLayout>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box component="fieldset" sx={{ border: '1px dashed grey' }}>
                  <Legend>Local</Legend>
                  <SimpleShowLayout>
                    <ReferenceField
                      label="Posto"
                      reference="stations"
                      source="placeId"
                      filter={{ chainId: getChainId() }}
                      link={permissions && permissions.includes(PERMISSIONS.ADMIN) && "show"}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                    <ReferenceField
                      label="Frentista"
                      emptyText="Não identificado"
                      source="attendantId"
                      reference="attendants"
                      link={permissions && permissions.includes(PERMISSIONS.UPDATE_ATTENDANT) && "show"}
                    >
                      <FunctionField render={record => formatAttendantLabel(record)} />
                    </ReferenceField>
                  </SimpleShowLayout>
                </Box>
                <Box component="fieldset" sx={{ border: '1px dashed grey', marginTop: 2 }}>
                  <Legend>Avaliação</Legend>
                  <ReferenceManyField
                    source="id"
                    target="fillinId"
                    reference="ratings"
                    label="Avaliação"
                  >
                    <SingleFieldList linkType={false} empty={<Typography variant="body2" textAlign="center">Avaliação não encontrada</Typography>}>
                      <SimpleShowLayout>
                        <StarRatingField source="attendantRating" label="Avaliação do frentista" />
                        <StarRatingField source="stationRating" label="Avaliação do Posto" />
                        <TextField source="comment" label="Comentário" />
                      </SimpleShowLayout>
                    </SingleFieldList>
                  </ReferenceManyField>
                </Box>
                <Box component="fieldset" sx={{ border: '1px dashed grey', marginTop: 2 }}>
                  <Legend>Informações técnicas</Legend>
                  <SimpleShowLayout>
                    <FunctionField
                      source="origin"
                      label="Origem"
                      render={record => FillinOrigin.translateOrigin(record ? record.origin : null)}
                    />
                    <ReferenceField
                      source="createdByEmployeeId"
                      reference="employees"
                      label="Criado por"
                      emptyText="--"
                      link={permissions?.includes(PERMISSIONS.UPDATE_USER) ? "show" : false}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                    <TextField source="appVersion" emptyText='--' label="Versão do dispositivo" />
                    {Permission({ permission: PERMISSIONS.ADMIN, children: <TextField source="deviceUniqueId" emptyText='--' label="ID do dispositivo" /> })}
                    {Permission({ permission: PERMISSIONS.ADMIN, children: <FunctionField label="Bluetooth conectado" render={record => record.bluetoothConnected == null ? '--' : record.bluetoothConnected ? 'Sim' : 'Não'} /> })}
                    {Permission({ permission: PERMISSIONS.ADMIN, children: <NumberField source="foregroundPercentage" emptyText='--' label="App em foco (%)" /> })}
                    {Permission({ permission: PERMISSIONS.ADMIN, children: <NumberField source="foregroundTimeMs" emptyText='--' label="App em foco (ms)" /> })}
                    {Permission({ permission: PERMISSIONS.ADMIN, children: <StationAutomationField field="brand" label="Marca da automação" /> })}
                    {Permission({ permission: PERMISSIONS.ADMIN, children: <StationAutomationField field="version" label="Versão da automação" /> })}
                    {Permission({ permission: PERMISSIONS.ADMIN, children: <StationAutomationField field="crtlVersion" label="Versão crtl" /> })}
                  </SimpleShowLayout>
                  <Permission permission={PERMISSIONS.ADMIN}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', paddingLeft: 2, paddingBottom: 1 }}>
                      A automação vem da última leitura diária do concentrador, não do instante do abastecimento.
                    </Typography>
                  </Permission>
                </Box>
              </Grid>
            </Grid>
          </SimpleShowLayout>
        </Tab>
        <Permission permission={PERMISSIONS.ADMIN}>
          <Tab label="Logs" path="logs">
            <FillinLogsTab />
          </Tab>
        </Permission>
      </TabbedShowLayout>
    </Show>
  );
}

const Legend = styled('legend')({
  paddingLeft: 12,
  paddingRight: 12,
});

export default FillinShow;
